import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/store/api';
import type { CallHistoryItem } from '@/types/call';
import type { DisplayCall } from '@/components/contact-center/CallHistoryCardTypes';
import { CallHistoryCallDetailsDialog } from '@/components/contact-center/CallHistoryCallDetailsDialog';
import { CallHistoryCaseDetailsDialog } from '@/components/contact-center/CallHistoryCaseDetailsDialog';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import {
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Container,
  Dropdown,
} from '@gapartners/ui';
import { getEnv } from '../../utils/env';

const CALLS_PER_PAGE = 20;

interface AgentOption extends Record<string, unknown> {
  id: string;
  label: string;
}

export function CallHistoryManagerView() {
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [isAgentSelectionInitialized, setIsAgentSelectionInitialized] =
    useState(false);
  const [selectedCall, setSelectedCall] = useState<DisplayCall | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [allCalls, setAllCalls] = useState<CallHistoryItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [selectedCaseContactId, setSelectedCaseContactId] = useState<
    string | null
  >(null);
  const [selectedCaseWorkspaceId, setSelectedCaseWorkspaceId] = useState<
    number | null
  >(null);

  const { t } = useTranslation();
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id);
  const {
    useGetDirectReportsQuery,
    useGetCallsByAgentManagerQuery,
    useGetCaseByIdQuery,
  } = api;

  const {
    data: caseData,
    isLoading: isCaseLoading,
    error: caseError,
  } = useGetCaseByIdQuery(
    { caseId: selectedCaseId! },
    { skip: selectedCaseId === null }
  );

  // Fetch direct reports (agents managed by current manager)
  const { data: initialDirectReports, isLoading: isLoadingAgents } =
    useGetDirectReportsQuery();
  const directReports = useMemo(() => {
    if (!initialDirectReports) return [];
    const useDummyData = getEnv('USE_DUMMY_DATA') === 'true';
    const source = useDummyData
      ? [
          ...initialDirectReports,
          {
            id: 'd9e931e0-51ee-4456-9bec-ad4e59d72d65',
            displayName: 'Mock Mykyta',
          },
          {
            id: 'd70b3e95-a1a9-4bdc-a576-e15374c17cfd',
            displayName: 'Mock Parikshit',
          },
          {
            id: '96f1782d-1567-4d9a-a4be-3c151779cc1d',
            displayName: 'Mock Manisha',
          },
        ]
      : initialDirectReports;
    return source.filter(agent => agent.id !== currentUserId);
  }, [initialDirectReports, currentUserId]);
  // Fetch calls only when an agent is selected
  const {
    data: callsResponse,
    isLoading: isLoadingCalls,
    isFetching: isFetchingCalls,
    error: callsError,
  } = useGetCallsByAgentManagerQuery(
    {
      agentIds: selectedAgentIds,
      limit: CALLS_PER_PAGE,
      cursor,
    },
    {
      skip: selectedAgentIds.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  // Merge new calls into existing list
  useEffect(() => {
    if (!callsResponse?.calls) return;

    if (!hasLoadedInitial) {
      setAllCalls(callsResponse.calls);
      setHasLoadedInitial(true);
    } else if (cursor) {
      setAllCalls(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newCalls = callsResponse.calls.filter(
          c => !existingIds.has(c.id)
        );
        return [...prev, ...newCalls];
      });
    }
  }, [callsResponse, cursor, hasLoadedInitial]);

  // Reset pagination when agent selection changes
  useEffect(() => {
    setAllCalls([]);
    setCursor(undefined);
    setHasLoadedInitial(false);
  }, [selectedAgentIds]);

  const hasMore = callsResponse?.hasMore ?? false;
  const nextCursor = callsResponse?.nextCursor ?? null;

  // Convert agent list for dropdown
  const agentOptions = useMemo(() => {
    if (!directReports) return [];
    return directReports.map(agent => ({
      id: agent.id,
      label: agent.displayName,
    }));
  }, [directReports]);

  const selectedAgentOptions = useMemo(
    () => agentOptions.filter(option => selectedAgentIds.includes(option.id)),
    [agentOptions, selectedAgentIds]
  );

  useEffect(() => {
    if (directReports.length === 0 || isAgentSelectionInitialized) return;

    setSelectedAgentIds(directReports.map(agent => agent.id));
    setIsAgentSelectionInitialized(true);
  }, [directReports, isAgentSelectionInitialized]);

  // Handle agent selection
  const handleAgentChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _event: any,
    selectedOptions: AgentOption[] | AgentOption | null
  ) => {
    const normalizedOptions = Array.isArray(selectedOptions)
      ? selectedOptions
      : selectedOptions
        ? [selectedOptions]
        : [];
    setSelectedAgentIds(normalizedOptions.map(option => option.id));
  };

  // Convert API call to display format
  const convertToDisplayCall = useCallback(
    (call: CallHistoryItem): DisplayCall => {
      return {
        id: call.id,
        contactId: call.contactId,
        type: call.type,
        dateTime: new Date(call.startDate).toLocaleString(),
        phoneNumber: call.phoneNumber,
        clientDetails: call.clientDetails,
        caseId: call.caseId,
        pickedBy: call.pickedBy,
        startDate: call.startDate,
        endDate: call.endDate,
        reason: call.reason ?? null,
        outcome: call.outcome ?? null,
        caseStatus: call.caseStatus ?? null,
        hasRecording: call.hasRecording,
        workspaceId: call.workspaceId,
      };
    },
    []
  );

  // Get call status color
  const getStatusBadgeStyle = (
    isLive: boolean
  ): { bgcolor: string; color: string } => {
    return isLive
      ? { bgcolor: '#dcfce7', color: '#166534' }
      : { bgcolor: '#e0e7ff', color: '#3730a3' };
  };

  // Get outcome color
  const getOutcomeColor = (
    outcome: string | null
  ): { bgcolor: string; color: string } => {
    if (!outcome) return { bgcolor: '#f3f4f6', color: '#6b7280' };
    const lowerOutcome = outcome.toLowerCase();
    if (lowerOutcome === 'resolved') {
      return { bgcolor: '#dcfce7', color: '#166534' };
    }
    if (lowerOutcome === 'escalated') {
      return { bgcolor: '#fee2e2', color: '#991b1b' };
    }
    if (lowerOutcome === 'pending') {
      return { bgcolor: '#fef9c3', color: '#854d0e' };
    }
    return { bgcolor: '#e0e7ff', color: '#3730a3' };
  };

  const getCaseStatusColor = (
    caseStatus: string | null
  ): { bgcolor: string; color: string } => {
    if (!caseStatus) return { bgcolor: '#f3f4f6', color: '#6b7280' };
    const lowerStatus = caseStatus.toLowerCase();
    if (lowerStatus === 'open' || lowerStatus === 'new') {
      return { bgcolor: '#dcfce7', color: '#166534' };
    }
    if (lowerStatus === 'closed') {
      return { bgcolor: '#e0e7ff', color: '#3730a3' };
    }
    return { bgcolor: '#fef9c3', color: '#854d0e' };
  };

  const handleLoadMore = () => {
    if (nextCursor) {
      setCursor(nextCursor);
    }
  };

  const displayCalls = allCalls.map(convertToDisplayCall);

  return (
    <Box sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        {/* Agent Selection Section */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ minWidth: 200, maxWidth: 400 }}>
            <Dropdown
              multiple
              options={agentOptions}
              value={selectedAgentOptions}
              onChange={handleAgentChange}
              getOptionId={option => option.id}
              getOptionLabel={option => option.label}
              textFieldProps={{
                label: t('pages.manager.callHistory.selectAgent'),
              }}
              disabled={isLoadingAgents}
            />
          </Box>
        </Paper>

        {/* Calls List Section */}
        {isLoadingAgents ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedAgentIds.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {t('pages.manager.callHistory.selectAgentPrompt')}
            </Typography>
          </Paper>
        ) : isLoadingCalls ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : callsError ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography color="error">
              {t('pages.manager.callHistory.errorLoadingCalls')}
            </Typography>
          </Paper>
        ) : displayCalls.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {t('pages.manager.callHistory.noCallsFound')}
            </Typography>
          </Paper>
        ) : (
          <Box>
            {/* Calls List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {displayCalls.map(call => (
                <Paper
                  key={call.id}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: 1,
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={() => setSelectedCall(call)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    {/* Call Info */}
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {call.clientDetails?.name || call.phoneNumber}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 0.5 }}
                      >
                        {call.dateTime}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {call.pickedBy?.[0]?.agentName || 'Unknown'}
                      </Typography>
                    </Box>

                    {/* Badges */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {/* Status Badge */}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          ...getStatusBadgeStyle(!call.endDate),
                        }}
                      >
                        {!call.endDate
                          ? t('pages.manager.callHistory.live')
                          : t('pages.manager.callHistory.historic')}
                      </Box>

                      {/* Outcome Badge */}
                      {call.outcome && (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            ...getOutcomeColor(call.outcome),
                          }}
                        >
                          {call.outcome}
                        </Box>
                      )}

                      {/* Case Status Badge */}
                      {call.caseStatus && (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: '#f3f4f6',
                            color: '#6b7280',
                          }}
                        >
                          {call.caseStatus}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Load More Button */}
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  onClick={handleLoadMore}
                  disabled={isFetchingCalls}
                  variant="outlined"
                >
                  {isFetchingCalls ? t('common.loading') : t('common.loadMore')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        <CallHistoryCallDetailsDialog
          selectedCall={selectedCall}
          showTranscript={showTranscript}
          setShowTranscript={setShowTranscript}
          onClose={() => setSelectedCall(null)}
          getOutcomeColor={getOutcomeColor}
          getCaseStatusColor={getCaseStatusColor}
          onOpenCaseId={(caseId, contactId) => {
            setSelectedCaseId(caseId);
            setSelectedCaseContactId(contactId);
            setSelectedCaseWorkspaceId(
              selectedCall?.workspaceId
                ? Number(selectedCall.workspaceId)
                : null
            );
          }}
          enableLiveMonitoring={true}
        />

        <CallHistoryCaseDetailsDialog
          selectedCaseId={selectedCaseId}
          contactId={selectedCaseContactId}
          workspaceId={selectedCaseWorkspaceId}
          onClose={() => {
            setSelectedCaseId(null);
            setSelectedCaseContactId(null);
            setSelectedCaseWorkspaceId(null);
          }}
          isCaseLoading={isCaseLoading}
          caseError={caseError}
          caseData={caseData}
        />
      </Container>
    </Box>
  );
}
